import { useCallback, useEffect, useState } from 'react'

import { getTemperatureSizeSales } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import {
  SizeModel,
  SizeResponseModel,
  StyleModel,
  StyleResponseModel,
  TemperatureSizeSalesModel,
} from '@/types/clarityLens'

type ChartDataModel = {
  data: Record<string, number>
  labels: string[]
}

export default function useTemperatureSizeSales(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [salesByStyleChartData, setSalesByStyleChartData] =
    useState<ChartDataModel>()
  const [salesBySizeChartData, setSalesBySizeChartData] =
    useState<ChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!itemGroupName) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data } = await fetchTemperatureSizeSalesData(
        branches,
        startDate,
        endDate,
        itemGroupName
      )

      const salesByStyleChartData: ChartDataModel = data?.salesByStyle
        ? {
            data: Object.fromEntries(
              data.salesByStyle.map((style) => [
                style.style,
                style.netSalesAmount,
              ])
            ),
            labels: data.salesByStyle.map((style) => style.style),
          }
        : { data: {}, labels: [] }

      const salesBySizeChartData: ChartDataModel = data?.salesBySize
        ? {
            data: Object.fromEntries(
              data.salesBySize.map((size) => [size.size, size.netSalesAmount])
            ),
            labels: data.salesBySize.map((size) => size.size),
          }
        : { data: {}, labels: [] }

      setSalesByStyleChartData(salesByStyleChartData)
      setSalesBySizeChartData(salesBySizeChartData)
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [branches, startDate, endDate, itemGroupName])

  const refetch = useCallback(() => {
    console.log('Refetching temperature size sales...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    salesByStyleChartData,
    salesBySizeChartData,
    loading,
    error,
    refetch,
  }
}

async function fetchTemperatureSizeSalesData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string
): Promise<{
  data: TemperatureSizeSalesModel
}> {
  try {
    const response = await getTemperatureSizeSales(
      branches,
      startDate,
      endDate,
      itemGroupName
    )

    const data: TemperatureSizeSalesModel = convertData(
      response.sales_by_size,
      response.sales_by_style
    )

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  salesBySize: SizeResponseModel[],
  salesByStyle: StyleResponseModel[]
): TemperatureSizeSalesModel {
  const salesBySizeData: SizeModel[] = salesBySize.map((size) => {
    return {
      size: size.size,
      netSalesAmount: formatMetric(size.net_sales_amount, 2),
    }
  })

  const salesByStyleData: StyleModel[] = salesByStyle.map((style) => {
    return {
      style: style.style,
      netSalesAmount: formatMetric(style.net_sales_amount, 2),
    }
  })
  return {
    salesBySize: salesBySizeData,
    salesByStyle: salesByStyleData,
  }
}
